import { useState, useEffect, RefObject } from 'react';

interface Room {
  id?: string | number;
  patients?: any[];
}

interface UsePanelDistributionProps {
  containerRef: RefObject<HTMLElement>;
  waitingRoom?: Room;
  preOp?: Room;
  pacu?: Room;
  postOps?: Room[];
  hasPostop?: boolean;
  carouselSize?: number;
  messagingView?: boolean;
  topRowHeight?: number;
  isKiosk?: boolean;
}

// Configuration constants
const MIN_PANEL_WIDTH = 350; // Minimum width per panel column in pixels
const GLOBAL_HEADER_HEIGHT = 100; // Ospitek header + margins (fixed viewport offset)
const PANEL_HEADER_HEIGHT = 60; // Non-OR Room header height
const LINE_HEIGHT_MULTIPLIER = 1.5;
const ROW_VERTICAL_PADDING = 28;
const DEFAULT_FONT_SIZE = 16;
const DEFAULT_ITEM_HEIGHT = 80;

export const usePanelDistribution = ({
  containerRef,
  waitingRoom,
  preOp,
  pacu,
  postOps,
  hasPostop,
  topRowHeight = 0,
  isKiosk = false,
}: UsePanelDistributionProps) => {
  // Track container width via ResizeObserver (for panel count)
  const [containerWidth, setContainerWidth] = useState(0);
  const [baseFontSize, setBaseFontSize] = useState(DEFAULT_FONT_SIZE);
  const [measuredItemHeight, setMeasuredItemHeight] = useState<number | null>(null);

  // Track viewport height (for carousel size - NOT container height which grows with content)
  const [viewportHeight, setViewportHeight] = useState(typeof window !== 'undefined' ? window.innerHeight : 900);

  // ResizeObserver for container WIDTH only
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver(entries => {
      const { width } = entries[0].contentRect;
      setContainerWidth(width);

      if (containerRef.current) {
        // Select all slots currently in the DOM
        const slots = containerRef.current.querySelectorAll('[data-patient-slot="true"]');

        if (slots.length >= 2) {
          const firstRect = slots[0].getBoundingClientRect();
          const secondRect = slots[1].getBoundingClientRect();
          const stride = secondRect.top - firstRect.top;

          // Guard: Ensure stride is a valid, positive number
          if (Number.isFinite(stride) && stride > 0) {
            setMeasuredItemHeight(stride);
          }
        } else if (slots.length === 1) {
          const slot = slots[0] as HTMLElement;
          const style = window.getComputedStyle(slot);
          const marginBottom = parseFloat(style.marginBottom) || 0;
          const marginTop = parseFloat(style.marginTop) || 0;

          const parentStyle = window.getComputedStyle(slot.parentElement!);
          const parentGap = parseFloat(parentStyle.gap) || 0;

          const calculatedHeight = slot.offsetHeight + marginBottom + marginTop + parentGap;

          // Guard: Ensure calculated height won't cause a division by zero
          if (Number.isFinite(calculatedHeight) && calculatedHeight > 0) {
            setMeasuredItemHeight(calculatedHeight);
          }
        }
      }
    });

    observer.observe(containerRef.current);

    // Initial measurement
    const rect = containerRef.current.getBoundingClientRect();
    setContainerWidth(rect.width);
    if (containerRef.current) {
      const fontSize = parseFloat(window.getComputedStyle(containerRef.current).fontSize);
      setBaseFontSize(Number.isFinite(fontSize) ? fontSize : DEFAULT_FONT_SIZE);
      const slot = containerRef.current.querySelector('[data-patient-slot="true"]');
      if (slot) {
        const rect = slot.getBoundingClientRect();
        const style = window.getComputedStyle(slot);
        const marginBottom = parseFloat(style.marginBottom) || 0;
        const nextHeight = rect.height + marginBottom;
        if (nextHeight > 0) {
          setMeasuredItemHeight(nextHeight);
        }
      }
    }

    return () => observer.disconnect();
  }, [containerRef]);

  // Window resize listener for VIEWPORT height
  useEffect(() => {
    const handleResize = () => setViewportHeight(window.innerHeight);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const nonOrRoomsCount =
    (waitingRoom?.id ? 1 : 0) +
    (preOp?.id ? 1 : 0) +
    (pacu?.id ? 1 : 0) +
    (hasPostop && postOps && Array.isArray(postOps) ? postOps.filter(postOp => postOp.id).length : 0);

  // Calculate MAX_PANELS from actual container width
  const baseMaxPanels = containerWidth > 0 ? Math.max(1, Math.floor(containerWidth / MIN_PANEL_WIDTH)) : 4; // Fallback for SSR or initial render
  const MAX_PANELS = nonOrRoomsCount > 2 ? 1 : baseMaxPanels;
  const isSingleColumnByWidth = baseMaxPanels === 1;

  // Count fixed panels (PACU and PostOp rooms)
  let fixedPanelsUsed = 0;
  if (pacu && pacu.id) fixedPanelsUsed += 1;
  const postOpCount = hasPostop && postOps && Array.isArray(postOps) ? postOps.filter(p => p.id).length : 0;
  fixedPanelsUsed += postOpCount;

  const stackedPanels = !isKiosk
    ? 1 // Non-kiosk: page scrolls, no need to divide height among sections
    : isSingleColumnByWidth
      ? Math.max(1, fixedPanelsUsed + (waitingRoom?.id ? 1 : 0) + (preOp?.id ? 1 : 0))
      : 1;

  // Calculate carousel size from VIEWPORT height (not container height!)
  // This prevents the feedback loop where content grows → container grows → more items
  const availableHeight =
    Math.max(0, viewportHeight - GLOBAL_HEADER_HEIGHT - PANEL_HEADER_HEIGHT - topRowHeight) / stackedPanels;
  const fallbackItemHeight = baseFontSize * LINE_HEIGHT_MULTIPLIER + ROW_VERTICAL_PADDING;
  const itemHeight = measuredItemHeight ?? fallbackItemHeight ?? DEFAULT_ITEM_HEIGHT;
  const calculatedItems = Math.floor(availableHeight / itemHeight);

  // Safety: Ensure minimum items shown (kiosk: 2 for carousel rotation, non-kiosk: 3 for usable display)
  const minItems = isKiosk ? 2 : 3;
  const dynamicSize = Math.max(minItems, calculatedItems);

  // Calculate available panels for flex groups (WaitingRoom, PreOp)
  const maxAvailablePanels = Math.max(0, MAX_PANELS - fixedPanelsUsed);

  // Helper to calculate needed panels for a room
  const getNeededPanels = (monitorRoom: Room | undefined, viewCarouselSize: number) => {
    if (!monitorRoom || !monitorRoom.id) return 0;
    const patientCount = monitorRoom.patients ? monitorRoom.patients.length : 0;
    if (patientCount === 0) return 1; // Always show at least 1 panel if room exists (for empty state)
    return Math.ceil(patientCount / viewCarouselSize);
  };

  // Use dynamic size as the source of truth for zoomed-in views, otherwise cap at 7 (supports 4K TVs)
  const actualCarouselSize = nonOrRoomsCount > 2 ? Math.min(dynamicSize, 7) : dynamicSize;

  const wrNeeded = getNeededPanels(waitingRoom, actualCarouselSize);
  const preOpNeeded = getNeededPanels(preOp, actualCarouselSize);

  let wrPanels = 0;
  let preOpPanels = 0;

  if (waitingRoom && waitingRoom.id && (!preOp || !preOp.id)) {
    // Only WR
    wrPanels = Math.min(maxAvailablePanels, wrNeeded);
  } else if ((!waitingRoom || !waitingRoom.id) && preOp && preOp.id) {
    // Only PreOp
    preOpPanels = Math.min(maxAvailablePanels, preOpNeeded);
  } else if (waitingRoom && waitingRoom.id && preOp && preOp.id) {
    // Both exist.
    // If we have plenty of space, give both what they need.
    if (wrNeeded + preOpNeeded <= maxAvailablePanels) {
      wrPanels = wrNeeded;
      preOpPanels = preOpNeeded;
    } else {
      // Scarcity.
      if (maxAvailablePanels >= 2) {
        // Allocate 1 to each minimum.
        wrPanels = 1;
        preOpPanels = 1;
        let remaining = maxAvailablePanels - 2;

        // Distribute remaining based on need
        while (remaining > 0) {
          if (wrPanels < wrNeeded && preOpPanels < preOpNeeded) {
            // Both want more. Give to WR first (arbitrary tie-break, usually larger)
            wrPanels++;
            remaining--;
          } else if (wrPanels < wrNeeded) {
            wrPanels++;
            remaining--;
          } else if (preOpPanels < preOpNeeded) {
            preOpPanels++;
            remaining--;
          } else {
            break; // Both satisfied
          }
        }
      } else {
        // Extreme Scarcity: Less than 2 panels for 2 rooms.
        // Give priority to Waiting Room if any panel is available.
        wrPanels = maxAvailablePanels > 0 ? 1 : 0;
        preOpPanels = 0;
      }
    }
  }

  // Fallback: Ensure at least 1 if they exist
  if (waitingRoom && waitingRoom.id && wrPanels === 0) wrPanels = 1;
  if (preOp && preOp.id && preOpPanels === 0) preOpPanels = 1;

  return {
    wrPanels,
    preOpPanels,
    MAX_PANELS,
    actualCarouselSize,
  };
};
